import { useRole } from '../api/role.ts';
import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { ApiRequestError, useAuth } from '../api/auth.ts';
import { Spinner } from '../components/ui.tsx';
import { LoginPage } from '../features/auth/LoginPage.tsx';
import { InvitationPage } from '../features/sites/InvitationPage.tsx';
import { TeamInvitationPage } from '../features/team/TeamInvitationPage.tsx';
import { PasswordResetPage } from '../features/reset/PasswordResetPage.tsx';
import { useIsTeamAdmin } from '../api/team.ts';
import { SitesPage } from '../features/sites/SitesPage.tsx';
import { NotificationsPage } from '../features/notifications/NotificationsPage.tsx';
import { ProtocolPage } from '../features/protocol/ProtocolPage.tsx';
import { SettingsPage } from '../features/settings/SettingsPage.tsx';
import { useConnection } from '../api/connection.ts';
import { BatteryPage } from '../features/battery/BatteryPage.tsx';
import { ChargingPage } from '../features/charging/ChargingPage.tsx';
import { DashboardPage } from '../features/dashboard/DashboardPage.tsx';
import { GridPage } from '../features/grid/GridPage.tsx';
import { HeatPumpPage } from '../features/heatpump/HeatPumpPage.tsx';
import { hasPermission, type CustomerPage, type Permission } from '@ste/shared';
import { useLocks } from '../api/customerView.ts';
import { t } from '../i18n/index.ts';
import { Layout } from './Layout.tsx';
import { initNativeAfterRender } from '../native/init.ts';

// recharts is only needed on the history page; the installer area only for installers
const InstallerPage = lazy(() =>
  import('../features/installer/InstallerPage.tsx').then((m) => ({ default: m.InstallerPage })),
);
const DeviceWizard = lazy(() =>
  import('../features/installer/DeviceWizard.tsx').then((m) => ({ default: m.DeviceWizard })),
);
const DeviceEditor = lazy(() =>
  import('../features/installer/DeviceWizard.tsx').then((m) => ({ default: m.DeviceEditor })),
);
const DiagnosticsPage = lazy(() =>
  import('../features/installer/DiagnosticsPage.tsx').then((m) => ({ default: m.DiagnosticsPage })),
);
const NetworkPage = lazy(() =>
  import('../features/installer/NetworkPage.tsx').then((m) => ({ default: m.NetworkPage })),
);
const HeatingPage = lazy(() =>
  import('../features/installer/HeatingPage.tsx').then((m) => ({ default: m.HeatingPage })),
);
const MaintenancePage = lazy(() =>
  import('../features/installer/MaintenancePage.tsx').then((m) => ({ default: m.MaintenancePage })),
);
const CustomerViewPage = lazy(() =>
  import('../features/installer/CustomerViewPage.tsx').then((m) => ({ default: m.CustomerViewPage })),
);
const TariffPage = lazy(() =>
  import('../features/installer/TariffPage.tsx').then((m) => ({ default: m.TariffPage })),
);
const WizardPage = lazy(() =>
  import('../features/installer/WizardPage.tsx').then((m) => ({ default: m.WizardPage })),
);
const ChecklistPage = lazy(() =>
  import('../features/installer/ChecklistPage.tsx').then((m) => ({ default: m.ChecklistPage })),
);
const TeamPage = lazy(() => import('../features/team/TeamPage.tsx').then((m) => ({ default: m.TeamPage })));
const HistoryPage = lazy(() =>
  import('../features/history/HistoryPage.tsx').then((m) => ({ default: m.HistoryPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => !(error instanceof ApiRequestError && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: false },
  },
});

function RequireAuth() {
  const status = useAuth((s) => s.status);
  if (status === 'unknown') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label={t('common.loading')} />
      </div>
    );
  }
  return status === 'authenticated' ? <Outlet /> : <Navigate to="/anmelden" replace />;
}

function RequirePermission({ permission }: { permission: Permission }) {
  const role = useRole();
  if (!role || !hasPermission(role, permission)) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<Spinner label={t('common.loading')} />}>
      <Outlet />
    </Suspense>
  );
}

/** STE Connect team management: global admins only */
function RequireTeamAdmin() {
  const admin = useIsTeamAdmin();
  if (!admin) return <Navigate to="/" replace />;
  return (
    <Suspense fallback={<Spinner label={t('common.loading')} />}>
      <TeamPage />
    </Suspense>
  );
}

function AnonymousOnly() {
  const status = useAuth((s) => s.status);
  return status === 'authenticated' ? <Navigate to="/" replace /> : <LoginPage />;
}

const router = createBrowserRouter([
  { path: '/anmelden', element: <AnonymousOnly /> },
  { path: '/einladung/:token', element: <InvitationPage /> },
  { path: '/team-einladung/:token', element: <TeamInvitationPage /> },
  { path: '/passwort-zuruecksetzen/:token', element: <PasswordResetPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Layout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'anlagen', element: <SitesPage /> },
          { path: 'team', element: <RequireTeamAdmin /> },
          { path: 'meldungen', element: <NotificationsPage /> },
          { path: 'protokoll', element: <ProtocolPage /> },
          { path: 'einstellungen', element: <SettingsPage /> },
          { path: 'laden', element: <CustomerPageGuard page="charging" element={<ChargingPage />} /> },
          {
            path: 'waermepumpe',
            element: <CustomerPageGuard page="heatpump" element={<HeatPumpPage />} />,
          },
          { path: 'speicher', element: <CustomerPageGuard page="battery" element={<BatteryPage />} /> },
          { path: 'netz', element: <CustomerPageGuard page="grid" element={<GridPage />} /> },
          {
            path: 'historie',
            element: (
              <CustomerPageGuard
                page="history"
                element={
                  <Suspense fallback={<Spinner label={t('common.loading')} />}>
                    <HistoryPage />
                  </Suspense>
                }
              />
            ),
          },
          {
            path: 'installateur',
            element: <RequirePermission permission="site:diagnose" />,
            children: [
              { index: true, element: <InstallerPage /> },
              { path: 'neu', element: <DeviceWizard /> },
              { path: 'bearbeiten/:name', element: <DeviceEditor /> },
              { path: 'diagnose', element: <DiagnosticsPage /> },
              { path: 'checkliste', element: <ChecklistPage /> },
              { path: 'assistent', element: <WizardPage /> },
              { path: 'netzwerk', element: <NetworkPage /> },
              { path: 'tarife', element: <TariffPage /> },
              { path: 'waerme', element: <HeatingPage /> },
              { path: 'kundensicht', element: <CustomerViewPage /> },
              { path: 'wartung', element: <MaintenancePage /> },
            ],
          },
          { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);

/** pages the installer hid for customers lead to the overview (ADR-0020) */
function CustomerPageGuard({ page, element }: { page: CustomerPage; element: ReactElement }) {
  const locks = useLocks();
  return locks.hidden(page) ? <Navigate to="/" replace /> : element;
}

export function App() {
  const restore = useAuth((s) => s.restore);
  useEffect(() => {
    void useConnection
      .getState()
      .init()
      .then(() => restore())
      .then(() => initNativeAfterRender((path) => void router.navigate(path)));
  }, [restore]);

  // site-scoped data must never leak into another site's view
  useEffect(
    () =>
      useConnection.subscribe((state, previous) => {
        if (state.siteId !== previous.siteId)
          queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'cloud' });
      }),
    [],
  );

  // drop cached data of the previous user on logout
  useEffect(
    () =>
      useAuth.subscribe((state, previous) => {
        if (previous.status === 'authenticated' && state.status !== 'authenticated') queryClient.clear();
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
